import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuModule } from 'primeng/menu';
import type { MenuItem } from 'primeng/api';
import { Editor, Extension, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';
import type { CommentAttachment } from '@tourfold/libraries/tf-api-client-public-v2';
import { CommentsService } from '../services/comments.service';
import {
  AttachmentUploadError,
  type AttachmentUploadErrorReason,
  CommentAttachmentsService,
  isBlockedMime,
} from '../services/comment-attachments.service';
import { extractAttachmentIds } from '../services/extract-attachment-ids';
import {
  encodeSizeIntoAlt,
  extractInlineImageSizes,
  parseSizeFromAlt,
} from '../services/inline-image-size';
import { FormatBytesPipe } from '../pipes/format-bytes.pipe';
import { buildMentionSuggestion } from './mention-suggestion';
import { UploadPlaceholder } from './upload-placeholder';

/**
 * Shape of the markdown storage slot that `tiptap-markdown` registers on `editor.storage`. The
 * package mutates storage at runtime but doesn't ship type augmentations, so we narrow it here
 * locally instead of polluting the global `Storage` interface.
 */
interface MarkdownStorage {
  readonly markdown: { getMarkdown: () => string };
}

/**
 * Heading levels exposed by the toolbar. Mirrors the server allow-list (1–3) — H4+ is rejected
 * at submit so we never offer it client-side.
 */
type HeadingLevel = 1 | 2 | 3;

interface ActiveMarks {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly strike: boolean;
  readonly code: boolean;
  readonly link: boolean;
  readonly blockquote: boolean;
  readonly bulletList: boolean;
  readonly orderedList: boolean;
  readonly codeBlock: boolean;
  /** Heading level 1, 2, or 3, or `null` when the selection is in plain paragraph text. */
  readonly headingLevel: HeadingLevel | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
}

const EMPTY_MARKS: ActiveMarks = {
  bold: false,
  italic: false,
  strike: false,
  code: false,
  link: false,
  blockquote: false,
  bulletList: false,
  orderedList: false,
  codeBlock: false,
  headingLevel: null,
  canUndo: false,
  canRedo: false,
};

/**
 * Markdown-in / markdown-out rich text editor backed by Tiptap (MIT). The component is the
 * editor library's only entry point — `<tf-comment-composer>` and `<tf-comment-edit-form>` only
 * see markdown strings on both sides of the contract, so swapping Tiptap for another editor
 * later is a one-component change with no migration of stored content.
 *
 * Storage / wire format = markdown (the backend column is `body_markdown`); Tiptap's native
 * ProseMirror tree is a transient in-memory representation, never persisted. The read path
 * (`<markdown>` from ngx-markdown) doesn't depend on Tiptap at all — only authoring does.
 *
 * Allow-list mirrors the server: bold, italic, inline code, fenced code blocks, lists,
 * blockquotes, http(s) links. Headings are deliberately disabled — the comments allow-list
 * doesn't include them. Image and mention extensions are intentionally absent in this slice;
 * they land alongside the upload + people-picker work in a follow-up.
 */
@Component({
  selector: 'tf-rich-editor',
  standalone: true,
  imports: [TranslateModule, MenuModule, FormatBytesPipe, CloseOverlayOnNavigationDirective],
  templateUrl: './rich-editor.component.html',
  styleUrl: './rich-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Tiptap injects its `.ProseMirror` contenteditable into the DOM at runtime, after the
  // template renders. Angular's emulated encapsulation only adds its scoping attribute to
  // elements declared in this component's template, so the dynamically-inserted contenteditable
  // wouldn't match any `.ProseMirror` selector under emulated mode (its outline / placeholder /
  // body styles would silently miss). All selectors in this component's stylesheet are already
  // scoped under `.tf-rich-editor` / `.tf-rich-editor__host`, so disabling encapsulation is
  // safe — the rules don't leak outside the editor.
  encapsulation: ViewEncapsulation.None,
})
export class RichEditorComponent implements AfterViewInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly commentsService = inject(CommentsService);
  private readonly attachmentsService = inject(CommentAttachmentsService);

  protected readonly editorRoot = viewChild.required<ElementRef<HTMLElement>>('editorRoot');
  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /**
   * Initial / external markdown content. The component reads this on init and reacts to
   * subsequent external changes (e.g. the edit form seeding the published body on open) — but
   * skips the round-trip when the input matches the editor's own current serialization, so
   * the user's caret + selection survive parent re-renders triggered by our own emit.
   */
  readonly bodyMarkdown = input<string>('');
  readonly placeholder = input<string>('');
  readonly autofocus = input<boolean>(false);
  readonly ariaLabel = input<string>('');

  /**
   * Existing attachments to seed the pending row with. Used by the edit form so every file on
   * the comment — inline images and non-inline alike — surfaces in the pill row. Each entry
   * becomes an `uploaded` pill (no progress, no Retry) that the user can remove via X.
   *
   * Image entries in this list are NOT re-inserted into the body — the body markdown already
   * carries inline references via `attachment:<uuid>`, which is the canonical place inline
   * images live. Pass the comment's full `attachments` array; the editor will surface inline
   * images both in the body and in the pill row (the pill is the "list view"), and the size
   * buttons only appear on pills whose underlying image is currently inline.
   *
   * Beyond seeding the pill row, the entries' `downloadUrl` / `thumbnailUrl` are used by the
   * inline `<img>` resolver — the editor renders `<img src=attachment:<uuid>>` nodes by
   * looking up the resolved URL here, so inline images actually display in edit mode.
   */
  readonly initialAttachments = input<readonly CommentAttachment[]>([]);

  /** Fired on every transaction (keystroke, paste, formatting toggle, etc.). */
  readonly bodyMarkdownChange = output<string>();

  /**
   * Fired when the user presses Cmd+Enter (or Ctrl+Enter on non-Mac). The host typically wires
   * this to its submit handler — plain Enter still inserts a newline, matching the Slack /
   * GitHub convention where the modifier is the explicit "send" affordance.
   */
  readonly submitShortcut = output<void>();

  /**
   * Fired when an attachment upload (drop, paste, or picker) fails with a typed reason. The
   * host renders the tailored message — most importantly the storage-allowance banner on
   * `storage-limit-exceeded`, which the editor itself can't surface inline without growing a
   * new toast surface. Per-file recovery (Retry, Remove) happens through the pending-pill row
   * inside the editor.
   *
   * `localId` correlates the banner with the underlying failed pill. The host uses it to:
   *  - call `removePending(localId)` when the user dismisses the banner (banner-dismiss → pill
   *    also goes away), and
   *  - clear the banner when {@link pendingRemoved} fires for the same id (pill-X → banner
   *    also goes away). Without the id, the two error surfaces drift apart and the user has
   *    to close both manually.
   */
  readonly uploadError = output<{ error: AttachmentUploadError; localId: string }>();

  /**
   * Fired whenever a pending entry is removed from the row — either by the user clicking the
   * pill's X, by the host calling {@link removePending}, or implicitly when the entry is
   * replaced (currently no such path; reserved for future). The host listens to clear any
   * banner currently keyed off this `localId`.
   */
  readonly pendingRemoved = output<string>();

  /**
   * Fires on any change to the union of attachment ids the host should send on submit:
   *   - inline `attachment:<uuid>` tokens currently in the body markdown
   *   - non-inline files in the pending row whose upload succeeded
   *
   * The composer / edit-form snapshot this on submit and forward it to the backend as
   * `attachment_ids`. Emitting on change (rather than asking the editor on submit) keeps the
   * host's signal graph simple — they bind the latest value into a signal and read it once.
   */
  readonly attachmentIdsChange = output<readonly string[]>();

  /** Reactive snapshot of the currently-active marks/blocks for toolbar pressed-state. */
  protected readonly active = signal<ActiveMarks>(EMPTY_MARKS);

  /**
   * Per-file pending state for the pill row above the editor. Pills appear the moment a file
   * enters the pipeline (drop / paste / picker) and serve as the upload-progress UI for every
   * MIME type. Lifecycle:
   *   - `uploading` — request in flight, shows a shimmer bar.
   *   - `uploaded` — only for non-image MIMEs: pill stays as the "attached" indicator and its
   *     `filestoreFileId` contributes to the `attachmentIdsChange` payload until the comment
   *     is posted. (Image MIMEs are removed from the pending list on success because their
   *     presence in the body markdown is the user's confirmation.)
   *   - `failed` — upload returned an `AttachmentUploadError`; pill renders Retry + Remove and
   *     the error reason is mirrored on the host via `uploadError`.
   *
   * We keep the original `File` around so Retry can re-submit without forcing the user to
   * re-pick.
   */
  protected readonly pending = signal<readonly PendingAttachment[]>([]);

  /**
   * Drag-overlay visibility. Set to `true` as soon as a file drag enters the editor wrapper,
   * cleared when the drag leaves or drops. The overlay covers the entire editor surface so the
   * user can drop anywhere inside the wrapper to attach — the file's MIME alone decides whether
   * the drop ends up inline (image at the current cursor position) or as a non-inline pill.
   * There's deliberately no per-position caret indicator: the cursor stays put during the drag
   * and the image lands wherever the cursor was before the drag started.
   */
  protected readonly dragState = signal<boolean>(false);

  /**
   * Re-entrant counter that survives the standard `dragleave-fires-on-every-child-boundary`
   * bug. Increment on `dragenter`, decrement on `dragleave`; clear `dragState` only when the
   * counter hits zero. Without this we get a flicker every time the dragged file crosses a
   * paragraph / span boundary inside the editor.
   */
  private dragEnterDepth = 0;

  /**
   * Option set for the s/m/l size buttons on inline-image pills. `short` is the visible label
   * (translated separately would be visual noise — single letters), `labelKey` is the
   * screen-reader-friendly form. Static because the values are part of the wire contract:
   * inserting the image with class `tf-img-{s|m|l}` is what the renderer + read-side CSS
   * actually keys on.
   */
  protected readonly imageSizeOptions: ReadonlyArray<{
    readonly value: PendingImageSize;
    readonly short: string;
    readonly labelKey: string;
  }> = [
    { value: 's', short: 'S', labelKey: 'COMMENTS.EDITOR.IMAGE_SIZE_SMALL' },
    { value: 'm', short: 'M', labelKey: 'COMMENTS.EDITOR.IMAGE_SIZE_MEDIUM' },
    { value: 'l', short: 'L', labelKey: 'COMMENTS.EDITOR.IMAGE_SIZE_LARGE' },
  ];

  /**
   * Template helper: `true` when the pill's failure is retry-able (network blip / timeout /
   * unknown). MIME / size / count / storage failures are deterministic and Retry would just
   * produce the same error, so the button is hidden — Remove is the only sensible action.
   */
  protected readonly isRecoverable = isRecoverableUploadFailure;

  /**
   * Label shown on the heading dropdown trigger — "Paragraph" for plain text, "Heading 1"
   * etc. for an active heading. Resolved through the translate service so it matches the
   * current language.
   */
  protected readonly headingLabel = computed<string>(() => {
    const level = this.active().headingLevel;
    if (level === null) return this.translate.instant('COMMENTS.EDITOR.HEADING_PARAGRAPH');
    return this.translate.instant(`COMMENTS.EDITOR.HEADING_${level}`);
  });

  /**
   * Items rendered inside the PrimeNG `Menu` popup attached to the heading dropdown.
   * Computed reactively so the active block is highlighted via `styleClass` and re-fires
   * commands against the *current* editor selection.
   */
  protected readonly headingMenuItems = computed<MenuItem[]>(() => {
    const level = this.active().headingLevel;
    return [
      {
        label: this.translate.instant('COMMENTS.EDITOR.HEADING_PARAGRAPH'),
        styleClass: level === null ? 'tf-rich-editor__menuitem--active' : undefined,
        command: () => this.setHeading(null),
      },
      {
        label: this.translate.instant('COMMENTS.EDITOR.HEADING_1'),
        styleClass: level === 1 ? 'tf-rich-editor__menuitem--active' : undefined,
        command: () => this.setHeading(1),
      },
      {
        label: this.translate.instant('COMMENTS.EDITOR.HEADING_2'),
        styleClass: level === 2 ? 'tf-rich-editor__menuitem--active' : undefined,
        command: () => this.setHeading(2),
      },
      {
        label: this.translate.instant('COMMENTS.EDITOR.HEADING_3'),
        styleClass: level === 3 ? 'tf-rich-editor__menuitem--active' : undefined,
        command: () => this.setHeading(3),
      },
    ];
  });

  private editor: Editor | null = null;

  /**
   * Last markdown string we emitted out via `bodyMarkdownChange`. Used to break the two-way
   * binding loop: when the parent mirrors the value back into `bodyMarkdown`, we recognise
   * our own emit and skip the editor `setContent` round-trip (which would clobber the user's
   * caret position and selection on every keystroke).
   */
  private lastEmittedMarkdown: string | null = null;

  /**
   * Live mirror of the editor's current body markdown. Updated from `onUpdate`, `setContent`,
   * and the initial seed. We need a *signal* (not the plain `lastEmittedMarkdown` field) so
   * `bodyInlineIds` can recompute reactively when the body changes — drives the per-pill
   * S/M/L visibility, which has to hide the moment the user Backspaces an inline image.
   */
  private readonly currentBodyMarkdown = signal<string>('');

  /**
   * Filestore ids currently referenced inline in the body markdown. Recomputes whenever the
   * body changes. Used by the template to gate the S/M/L size-button cluster on each image
   * pill — those buttons only make sense for an image that's actually inline (they control
   * the inline node's CSS class). An attachment-only image pill hides them.
   */
  protected readonly bodyInlineIds = computed<ReadonlySet<string>>(
    () => new Set(extractAttachmentIds(this.currentBodyMarkdown())),
  );

  /**
   * Object URLs for *just-uploaded* image files, keyed by their server-assigned
   * `filestoreFileId`. Used by the inline-image resolver so a freshly-inserted
   * `<img src=attachment:<uuid>>` actually displays: the server-side resolved URL doesn't
   * exist yet (it's generated when the comment is fetched back), so we render the local file
   * via {@link URL.createObjectURL} until then. Revoked on pill removal and on destroy.
   */
  private readonly localBlobUrls = new Map<string, string>();

  /**
   * One AbortController per in-flight upload, keyed by the pill's `localId`. Used by
   * {@link removePending} to abort the byte transfer when the user removes the pill mid-upload
   * (the upload-intent has resolved but the bytes are still streaming to object storage).
   * Cleared in `runUpload`'s finally block.
   */
  private readonly uploadAbortControllers = new Map<string, AbortController>();

  /** Tracks which set of `initialAttachments` we've already seeded so a re-render doesn't double-fill. */
  private seededAttachmentsKey: string | null = null;

  constructor() {
    // External `bodyMarkdown` changes (e.g. the edit form seeding the published body when the
    // user clicks Edit) need to sync into the editor. We wait until the editor is instantiated,
    // then call setContent only when the new value diverges from what we last emitted — that
    // guard keeps the cursor stable during normal typing.
    effect(() => {
      const next = this.bodyMarkdown();
      const ed = this.editor;
      if (!ed) return;
      if (next === this.lastEmittedMarkdown) return;
      // Tiptap-markdown's setContent path accepts markdown when the Markdown extension is
      // registered. Pass `false` so the change doesn't fire onUpdate (avoids re-emit loop).
      ed.commands.setContent(next ?? '', { emitUpdate: false });
      this.lastEmittedMarkdown = next;
      this.currentBodyMarkdown.set(next ?? '');
      this.refreshActive();
      // External body changes (draft restore, edit-mode seed) can introduce or remove inline
      // image tokens. Re-emit the attachment id union so the host's snapshot stays in lockstep
      // — otherwise an edit-mode seed without a follow-up keystroke would leave the host with
      // a stale empty array.
      this.emitAttachmentIds();
    });

    // Seed `pending` from the host's `initialAttachments` snapshot. Re-runs only when the input
    // identity actually changes (host re-renders with a new list); we de-dupe with
    // `seededAttachmentsKey` so a parent recomputing the same list doesn't repopulate and
    // overwrite the user's in-flight edits (Removed a pill, then parent re-renders → without
    // this guard the removed pill comes back).
    effect(() => {
      const list = this.initialAttachments();
      const key = list.map((a) => a.filestoreFileId).join('|');
      if (this.seededAttachmentsKey === key) return;
      this.seededAttachmentsKey = key;
      // Scan the body for `![filename|<size>](attachment:<uuid>)` tokens so each image pill
      // starts with the size the comment was last saved with. Pills for non-inline images, or
      // for legacy inline images without a size suffix, fall back to `'m'`.
      const sizesByFileId = extractInlineImageSizes(this.bodyMarkdown() ?? '');
      const seeded: PendingAttachment[] = list.map((a) => ({
        localId: nextLocalId(),
        name: a.name,
        sizeBytes: Number(a.sizeBytes) || 0,
        mimeType: a.mimeType,
        isImage: a.isImage,
        status: 'uploaded',
        filestoreFileId: a.filestoreFileId,
        size: a.isImage ? (sizesByFileId.get(a.filestoreFileId) ?? 'm') : null,
      }));
      this.pending.set(seeded);
      this.emitAttachmentIds();
    });
  }

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorRoot().nativeElement,
      autofocus: this.autofocus() ? 'end' : false,
      editorProps: {
        attributes: {
          class: 'tf-rich-editor__content',
          'aria-label': this.ariaLabel(),
        },
        // Drag-and-drop of files into the editor. `moved=true` means this is a same-editor
        // drag of an existing node (text/image) — let Tiptap handle the internal move, which is
        // how a user repositions an inline image inside the body.
        //
        // For external file drops we ignore the drop coordinates entirely and route everything
        // through `uploadFiles` — the file's MIME alone decides what happens:
        //  - image MIMEs land inline at the current cursor position (`selection.to`),
        //  - everything else attaches as a non-inline pill.
        // The "drop anywhere on the editor → same behavior" model is intentional: it removes
        // the need for the user to aim the drop precisely, which the per-position caret used to
        // require but felt finicky in practice.
        handleDrop: (_view, event, _slice, moved) => {
          if (moved) return false;
          const files = filesFromDataTransfer(event.dataTransfer);
          if (files.length === 0) return false;
          event.preventDefault();
          this.uploadFiles(files).catch(() => {/* errors propagate via uploadError */});
          return true;
        },
        // Paste (from clipboard / screenshot tool / drag from another tab). All MIME types are
        // accepted — non-images become pending pills, images additionally insert inline once the
        // upload resolves. Same MIME-decides-everything contract as drop.
        handlePaste: (_view, event) => {
          const files = filesFromDataTransfer(event.clipboardData);
          if (files.length === 0) return false;
          event.preventDefault();
          this.uploadFiles(files).catch(() => {/* errors propagate via uploadError */});
          return true;
        },
      },
      extensions: [
        // Headings are restricted to levels 1–3 — the server allow-list rejects H4+. Strike,
        // bold, italic, code, lists, blockquote, code block, history (undo/redo), and the
        // gapcursor / dropcursor essentials all come bundled in StarterKit.
        // StarterKit ships its own Link in v3, so disable it here and supply our explicit one
        // below — otherwise Tiptap warns about duplicate `link` extension names.
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          // Restrict to http(s) — mirror the server-side allow-list which strips other schemes.
          protocols: ['http', 'https'],
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        Placeholder.configure({ placeholder: this.placeholder() }),
        // Owns the DecorationSet of in-flight upload placeholders. See `upload-placeholder.ts`
        // for the lifecycle — set on drop/paste/picker at the user-aimed position, replaced with
        // the real `<img>` once the upload resolves, removed on failure or pill removal.
        // ProseMirror's transaction mapping keeps each placeholder at its original position
        // through any user edits during the upload window.
        UploadPlaceholder,
        // Inline image extension. We deliberately keep `allowBase64: false` so a stray paste of a
        // data-URI image bypasses the upload pipeline and never ends up in `body_markdown` — the
        // server-side allow-list would reject it anyway, but blocking at the source is cheaper.
        //
        // `src` carries the literal `attachment:<uuid>` token end-to-end on the wire:
        // tiptap-markdown writes `![alt](attachment:<uuid>)`, the server resolves the scheme to
        // a presigned URL at read time, and the read-path renderer never sees the original src.
        //
        // For the editor's *display* we override `renderHTML` to swap the token for a real URL
        // (object URL for just-uploaded files; resolved `downloadUrl`/`thumbnailUrl` for seeded
        // attachments) so inline images actually show in the contenteditable. The underlying
        // node attr stays as the token — tiptap-markdown reads `node.attrs.src`, not the
        // rendered DOM, so the serializer still emits the canonical token form.
        this.buildResolvingImageExtension().configure({
          inline: true,
          allowBase64: false,
          // `decoding="async"` keeps multi-MB raster images from blocking the main thread while
          // the browser decodes them — the editor would otherwise freeze for 1-2 s after a drop
          // when the placeholder is swapped for the real `<img>` carrying the same blob URL.
          HTMLAttributes: { class: 'tf-rich-editor__image', loading: 'lazy', decoding: 'async' },
        }),
        // @-mentions. The node carries `id` + `type` + `label` attributes and writes the same
        // `@[label](type:uuid)` wire format the backend extractor reads at create/edit time.
        //
        // tiptap-markdown picks the serializer up via `extension.storage.markdown.serialize`
        // (see `tiptap-markdown/src/util/extensions.js#getMarkdownSpec`). Without an explicit
        // entry, unknown nodes fall through to the HTML-fallback path which writes the literal
        // string `[mention]` — that's why `renderText` alone isn't enough; it only feeds
        // `editor.getText()`, not `getMarkdown()`.
        //
        // Edit-side parsing of existing mention tokens back into chip nodes is deferred to a
        // follow-up — for v1 the tokens render as literal text inside the editor when an edit
        // begins, but persist correctly on submit because the backend re-extracts them.
        Mention.extend({
          addAttributes() {
            return {
              id: { default: null },
              label: { default: null },
              type: { default: 'user' },
            };
          },
          addStorage() {
            return {
              markdown: {
                serialize(state: { write: (text: string) => void }, node: { attrs: Record<string, unknown> }): void {
                  const { id, label, type } = node.attrs as { id: string; label: string; type: string };
                  state.write(`@[${label}](${type}:${id})`);
                },
                parse: {},
              },
            };
          },
        }).configure({
          HTMLAttributes: { class: 'tf-mention-node' },
          renderText: ({ node }) => {
            const { id, label, type } = node.attrs as { id: string; label: string; type: string };
            return `@[${label}](${type}:${id})`;
          },
          suggestion: buildMentionSuggestion(this.commentsService, {
            noMatches: this.translate.instant('COMMENTS.MENTION.NO_MATCHES'),
          }),
        }),
        Markdown.configure({
          html: false,
          breaks: true,
          transformPastedText: true,
          transformCopiedText: true,
        }),
        // Cmd+Enter (Ctrl+Enter on non-Mac) → submit. Plain Enter keeps its native newline
        // behaviour. The `Mod-` prefix is ProseMirror's portable modifier alias and resolves to
        // Cmd on macOS and Ctrl elsewhere. Returning `true` consumes the event so the keystroke
        // doesn't also insert a paragraph break.
        Extension.create({
          name: 'tfSubmitShortcut',
          addKeyboardShortcuts: () => ({
            'Mod-Enter': () => {
              this.submitShortcut.emit();
              return true;
            },
          }),
        }),
      ],
      content: this.bodyMarkdown() ?? '',
      onUpdate: ({ editor }) => {
        const md = (editor.storage as unknown as MarkdownStorage).markdown.getMarkdown();
        this.lastEmittedMarkdown = md;
        this.currentBodyMarkdown.set(md);
        this.bodyMarkdownChange.emit(md);
        this.refreshActive();
      },
      onSelectionUpdate: () => this.refreshActive(),
    });
    this.lastEmittedMarkdown = this.bodyMarkdown() ?? '';
    this.currentBodyMarkdown.set(this.bodyMarkdown() ?? '');
    this.refreshActive();
    // Initial seed of `initialAttachments` may have run before the editor existed (the
    // constructor effect bailed via `if (!ed) return`). Re-emit now that we have both the
    // editor and the seeded pill row.
    this.emitAttachmentIds();
  }

  /**
   * Builds the Image extension with a custom `renderHTML` that swaps the canonical
   * `attachment:<uuid>` `src` for a displayable URL at render time. The closure captures the
   * component so {@link resolveAttachmentSrc} can read live signals (initial attachments,
   * upload-time blob URLs). The underlying node attribute stays as the token — tiptap-markdown
   * serializes from `node.attrs.src`, so the wire output is unchanged.
   */
  private buildResolvingImageExtension(): typeof Image {
    const resolve = (id: string): string | undefined => this.resolveAttachmentSrc(id);
    return Image.extend({
      renderHTML({ HTMLAttributes }) {
        const merged = mergeAttributes(this.options.HTMLAttributes as Record<string, unknown>, HTMLAttributes);

        // Derive the size CSS class from the alt suffix. `class` is *not* stored on the node —
        // alt is the canonical wire-form carrier of the size choice (the markdown allow-list
        // only constrains the destination scheme, not alt text), so deriving here at render
        // time keeps the node attrs minimal and avoids a duplicate source of truth.
        const altRaw = merged['alt'];
        if (typeof altRaw === 'string') {
          const { displayAlt, size } = parseSizeFromAlt(altRaw);
          if (size) {
            merged['alt'] = displayAlt;
            const existingClass = (merged['class'] as string | undefined) ?? '';
            merged['class'] = existingClass ? `${existingClass} tf-img-${size}` : `tf-img-${size}`;
          }
        }

        const src = merged['src'];
        if (typeof src === 'string' && src.startsWith('attachment:')) {
          const id = src.slice('attachment:'.length);
          const resolved = resolve(id);
          if (resolved) {
            merged['src'] = resolved;
            merged['data-attachment-id'] = id;
          }
        }
        return ['img', merged];
      },
    });
  }

  /**
   * Looks up a displayable URL for the given attachment id. Order:
   *  1. Local object URL for a file we just uploaded (created in {@link runUpload}). This is
   *     the only path that exists for fresh composer / edit-mode uploads — the server-side URL
   *     isn't generated until the comment is fetched back.
   *  2. The `thumbnailUrl` / `downloadUrl` carried by a seeded `initialAttachments` entry —
   *     covers existing inline images on the comment being edited.
   *  3. `undefined` — caller leaves the canonical `attachment:<uuid>` src in place, which the
   *     browser renders as a broken icon (intentional: that signals "image not resolvable" and
   *     matches the pre-fix behaviour for completeness).
   */
  private resolveAttachmentSrc(filestoreFileId: string): string | undefined {
    const local = this.localBlobUrls.get(filestoreFileId);
    if (local) return local;
    for (const a of this.initialAttachments()) {
      if (a.filestoreFileId === filestoreFileId) {
        return a.thumbnailUrl ?? a.downloadUrl ?? undefined;
      }
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.editor = null;
    for (const url of this.localBlobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.localBlobUrls.clear();
  }

  protected undo(): void {
    this.editor?.chain().focus().undo().run();
  }
  protected redo(): void {
    this.editor?.chain().focus().redo().run();
  }
  protected toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }
  protected toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }
  protected toggleStrike(): void {
    this.editor?.chain().focus().toggleStrike().run();
  }
  protected toggleCode(): void {
    this.editor?.chain().focus().toggleCode().run();
  }
  protected toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
  }
  protected toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
  }
  protected toggleBlockquote(): void {
    this.editor?.chain().focus().toggleBlockquote().run();
  }
  protected toggleCodeBlock(): void {
    this.editor?.chain().focus().toggleCodeBlock().run();
  }

  /**
   * Set the block at the cursor to a heading of the given level, or to a paragraph when
   * `level === null`. Bound to the heading dropdown's items.
   */
  protected setHeading(level: HeadingLevel | null): void {
    const ed = this.editor;
    if (!ed) return;
    if (level === null) {
      ed.chain().focus().setParagraph().run();
      return;
    }
    ed.chain().focus().toggleHeading({ level }).run();
  }

  /**
   * Toolbar entry-point for picking image files. Delegates to the hidden `<input type="file">`
   * rather than implementing our own picker so we get the native OS dialog, drag-drop into the
   * picker, and accessibility for free. No-op when the editor is unmounted.
   */
  protected onAddImageRequested(): void {
    if (!this.editor) return;
    this.fileInput()?.nativeElement.click();
  }

  /**
   * `true` when the in-flight drag carries OS files (as opposed to a text selection drag or an
   * internal Tiptap node drag). `types` is the only universally-available signal during a
   * drag — Chrome populates `items` with kind/type, Firefox and Safari hide them for security,
   * but every browser puts `"Files"` in `types` for OS-file drags.
   */
  private dragCarriesFiles(transfer: DataTransfer | null): boolean {
    if (!transfer) return false;
    return Array.from(transfer.types).includes('Files');
  }

  protected onWrapperDragEnter(event: DragEvent): void {
    if (!this.dragCarriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    this.dragEnterDepth += 1;
    this.dragState.set(true);
  }

  protected onWrapperDragOver(event: DragEvent): void {
    if (!this.dragCarriesFiles(event.dataTransfer)) return;
    // Always `preventDefault` on dragover when a file is being dragged — without it the browser
    // refuses the drop entirely ("not a valid drop target") and then runs its native handler
    // (e.g. open the video file in a fullscreen player).
    event.preventDefault();
  }

  protected onWrapperDragLeave(): void {
    this.dragEnterDepth = Math.max(0, this.dragEnterDepth - 1);
    if (this.dragEnterDepth === 0) {
      this.dragState.set(false);
    }
  }

  protected onWrapperDrop(event: DragEvent): void {
    this.dragEnterDepth = 0;
    this.dragState.set(false);
    // If the drop landed on the ProseMirror element, Tiptap's `handleDrop` already ran
    // (calls `preventDefault`, kicks off `uploadFiles`). The event bubbles here with
    // `defaultPrevented=true` and there's nothing left for us to do.
    if (event.defaultPrevented) return;
    // Otherwise the drop landed on the host-wrap padding, the drop overlay, the pill row, or
    // anywhere outside the contenteditable. Default browser behaviour for an unclaimed file
    // drop is to navigate to the file (videos open fullscreen, images replace the page). We
    // claim it ourselves and route through the same `uploadFiles` path the contenteditable uses
    // — the per-file MIME decides inline-at-cursor vs attach-as-pill.
    event.preventDefault();
    const files = filesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) return;
    this.uploadFiles(files).catch(() => {/* errors propagate via uploadError */});
  }

  protected onImageFilesChosen(event: Event): void {
    const target = event.target as HTMLInputElement;
    const picked = Array.from(target.files ?? []);
    // Reset the input value so re-picking the same file fires a new `change` event.
    target.value = '';
    if (picked.length === 0 || !this.editor) return;
    this.uploadFiles(picked).catch(() => {/* errors propagate via uploadError */});
  }

  /**
   * Drop / paste / picker entry point. Adds a pending pill per file, inserts an inline
   * upload-placeholder widget at the editor's current selection for image MIMEs (so the
   * eventual `<img>` lands where the cursor was), then fires the network uploads in parallel.
   *
   * MIME alone decides what happens with each file:
   *  - `image/*` → inline insertion at the cursor + pill in the row,
   *  - everything else → pill only (attached but not inline).
   * The pill never disappears on upload success — that's the user's signal that the file is
   * attached, regardless of whether it's also embedded inline.
   *
   * Why the placeholder is dispatched here rather than per upload-handler: the cursor position
   * needs to be captured at the moment the user committed (drop/paste/picker). Dispatching the
   * placeholder synchronously, before any `await`, captures that position immediately;
   * ProseMirror's transaction mapping then carries the placeholder through whatever the user
   * does during the upload window.
   */
  private async uploadFiles(files: readonly File[]): Promise<void> {
    const placeholderPos = this.editor?.state.selection.to;
    const startedIds: string[] = [];
    for (const file of files) {
      const localId = nextLocalId();
      const isImage = file.type.startsWith('image/');
      const mimeType = file.type || 'application/octet-stream';

      // Mirror of the server's `comments.attachments.blocked-mime-types`. Reject locally so the
      // bytes never leave the browser — the user sees the same red-pill + banner as a server
      // reject in milliseconds. Server is still the source of truth: a file whose MIME the
      // browser couldn't determine (empty `file.type`) skips the check here and gets caught at
      // the upload-intent endpoint with the same problem-type.
      if (isBlockedMime(file.type)) {
        const error = new AttachmentUploadError('mime-not-allowed');
        const failedEntry: PendingAttachment = {
          localId,
          name: file.name,
          sizeBytes: file.size,
          mimeType,
          isImage,
          status: 'failed',
          error,
          size: null,
        };
        this.pending.update((arr) => [...arr, failedEntry]);
        this.uploadError.emit({ error, localId });
        this.emitAttachmentIds();
        continue;
      }

      const entry: PendingAttachment = {
        localId,
        name: file.name,
        sizeBytes: file.size,
        mimeType,
        isImage,
        status: 'uploading',
        file,
        size: isImage ? 'm' : null,
      };
      this.pending.update((arr) => [...arr, entry]);
      startedIds.push(localId);
      if (isImage && placeholderPos !== undefined) {
        this.insertUploadPlaceholder(localId, file, placeholderPos);
      }
    }
    // Fire all uploads in parallel; per-file state mutations land independently.
    await Promise.all(startedIds.map((id) => this.runUpload(id)));
  }

  private async runUpload(localId: string): Promise<void> {
    const entry = this.pending().find((p) => p.localId === localId);
    if (!entry?.file) return;
    const file = entry.file;
    const isImage = entry.isImage;

    const controller = new AbortController();
    this.uploadAbortControllers.set(localId, controller);

    try {
      const { filestoreFileId } = await this.attachmentsService.uploadFile(file, {
        signal: controller.signal,
        // Stash the id on the pending entry as soon as the intent step resolves. A mid-upload
        // pill removal then has something to delete server-side — without this hook the id is
        // only known here at the end of `await uploadFile`, by which point `removePending`
        // has already run and the local entry is gone.
        onIntent: ({ filestoreFileId: intentFileId }) => {
          this.pending.update((arr) =>
            arr.map((p) => (p.localId === localId ? { ...p, filestoreFileId: intentFileId } : p)),
          );
        },
      });
      // Re-read the pending entry — it may have been removed by the user mid-upload, in which
      // case we drop the result. `removePending` already tore down the placeholder; nothing
      // else to undo here.
      const current = this.pending().find((p) => p.localId === localId);
      if (!current) return;

      if (isImage) {
        // Cache an object URL for the just-uploaded image so the inline `<img>` actually
        // renders. The server-side resolved URL doesn't exist until the comment is fetched
        // back, so without this map the freshly-inserted token would render a broken icon.
        this.localBlobUrls.set(filestoreFileId, URL.createObjectURL(file));
        // Swap the upload-placeholder widget for the real image at the placeholder's *current*
        // mapped position — i.e. wherever the placeholder ended up after the user's edits
        // during the upload window.
        this.replaceUploadPlaceholder(localId, current.name, current.size ?? 'm', filestoreFileId);
      }
      this.pending.update((arr) =>
        arr.map((p) =>
          p.localId === localId
            ? { ...p, status: 'uploaded' as const, filestoreFileId }
            : p,
        ),
      );
    } catch (err) {
      // Caller-initiated abort (user removed the pill mid-upload). `removePending` has already
      // cleaned up the local state and queued the server-side delete if the intent had resolved.
      // Don't flip the pill to 'failed' or emit a banner — neither would have anywhere to land.
      if (err instanceof AttachmentUploadError && err.reason === 'aborted') return;
      const wrapped =
        err instanceof AttachmentUploadError ? err : new AttachmentUploadError('unknown', err);
      // Failure — pill flips to red and shows Retry. The placeholder isn't useful in that state
      // (there's no image to swap in), so we remove it; a successful retry will insert at the
      // current selection rather than the original drop spot.
      this.editor?.commands.removeUploadPlaceholder({ id: localId });
      this.pending.update((arr) =>
        arr.map((p) =>
          p.localId === localId
            ? { ...p, status: 'failed' as const, error: wrapped }
            : p,
        ),
      );
      this.uploadError.emit({ error: wrapped, localId });
    } finally {
      this.uploadAbortControllers.delete(localId);
      this.emitAttachmentIds();
    }
  }

  /**
   * Dispatches the `setUploadPlaceholder` command for the just-added pending file. Separated
   * out of `uploadFiles` so the placeholder management is the only place that touches the
   * `localBlobUrls` cache for in-flight uploads — gives `removePending` a single hook to
   * revoke the object URL whether the upload completes or is cancelled.
   */
  private insertUploadPlaceholder(localId: string, file: File, pos: number): void {
    const ed = this.editor;
    if (!ed) return;
    // Cache the local preview URL up-front under the local id so the placeholder widget can
    // render the user's chosen file inline immediately. We re-key under `filestoreFileId` on
    // upload completion (so the freshly-inserted `<img>` resolves through the same cache); on
    // failure / removal the preview gets revoked via `revokeUploadPlaceholderPreview`.
    const previewUrl = URL.createObjectURL(file);
    this.localBlobUrls.set(localPreviewKey(localId), previewUrl);
    ed.commands.setUploadPlaceholder({
      pos,
      spec: { id: localId, fileName: file.name, previewUrl },
    });
  }

  /**
   * Atomically swaps the placeholder widget for the real `<img>` node and revokes the
   * pre-upload preview URL (the resolver now picks up the post-upload one keyed under
   * `filestoreFileId`). No-op when the placeholder is gone.
   */
  private replaceUploadPlaceholder(
    localId: string,
    altText: string,
    size: PendingImageSize,
    filestoreFileId: string,
  ): void {
    const ed = this.editor;
    if (!ed) return;
    ed.commands.replaceUploadPlaceholder({
      id: localId,
      attrs: {
        src: `attachment:${filestoreFileId}`,
        // Encode the size choice into the alt suffix. `alt` is the wire carrier (lives in the
        // markdown body) so the size persists end-to-end without backend changes; the
        // extension's `renderHTML` derives the CSS class from this suffix at render time and
        // strips the suffix off the displayed alt so screen readers hear the clean filename.
        alt: encodeSizeIntoAlt(altText, size),
      },
    });
    this.revokeUploadPlaceholderPreview(localId);
  }

  /**
   * Revokes the local preview URL we attached to a placeholder widget. Idempotent.
   */
  private revokeUploadPlaceholderPreview(localId: string): void {
    const key = localPreviewKey(localId);
    const url = this.localBlobUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      this.localBlobUrls.delete(key);
    }
  }

  /**
   * Removes a pending entry. Aborts nothing — the in-flight upload finishes and is dropped.
   *
   * Public because the host (composer) calls it from its banner-dismiss handler to keep the
   * inline pill and the banner in lockstep: dismissing either surface removes both. The
   * `pendingRemoved` output fires after the state mutation so the host can mirror back any
   * banner state keyed off this `localId`.
   */
  removePending(localId: string): void {
    const entry = this.pending().find((p) => p.localId === localId);
    if (!entry) return; // Already gone — no-op, no event.
    this.pending.update((arr) => arr.filter((p) => p.localId !== localId));
    // Drop any in-flight upload-placeholder widget for this id. Cheap no-op when the placeholder
    // was never set (non-inline pill, drop-on-wrapper, retry-after-failure with selection
    // fallback) or has already been replaced with the real image.
    this.editor?.commands.removeUploadPlaceholder({ id: localId });
    this.revokeUploadPlaceholderPreview(localId);
    // Abort any in-flight upload for this pill. `runUpload`'s finally clears the map entry, and
    // its catch branch swallows the resulting `aborted` error so no failure pill / banner fires.
    this.uploadAbortControllers.get(localId)?.abort();
    if (entry.filestoreFileId) {
      // Strip any matching inline body token in the same gesture so body + attachment list
      // stay in lockstep. `removeInlineImageNode` is a no-op when no inline node references
      // the id (the attachment-only case), so this safely handles both inline and non-inline
      // image pills.
      this.removeInlineImageNode(entry.filestoreFileId);
      // The object URL for a just-uploaded image is no longer referenced by any DOM `<img>`
      // (the inline node was just deleted, and the pill itself is gone too). Revoke it so the
      // browser releases the file bytes — long composer sessions otherwise accumulate
      // unbounded blob memory across removed pills.
      const blob = this.localBlobUrls.get(entry.filestoreFileId);
      if (blob) {
        URL.revokeObjectURL(blob);
        this.localBlobUrls.delete(entry.filestoreFileId);
      }
      // Fire-and-forget server-side delete so the filestore blob is reclaimed immediately
      // instead of waiting up to 48 h for the nightly orphan-cleanup sweep. 404 (already
      // deleted / cross-tenant) and 409 (raced with a submit that linked the file) are both
      // benign for the caller — the reaper is the safety net for any network failures.
      //
      // Only fire for entries uploaded in *this* composer session (`entry.file` set). Entries
      // seeded from `initialAttachments` (edit form) are already linked to the posted comment
      // — the server would deterministically return 409, and the proper cleanup path is the
      // comment PATCH that diffs `attachment_ids` and cascades the blob delete.
      if (entry.file) {
        void this.attachmentsService
          .deleteAbandonedUpload(entry.filestoreFileId)
          .catch(() => {/* benign — reaper sweeps */});
      }
    }
    this.emitAttachmentIds();
    this.pendingRemoved.emit(localId);
  }

  /**
   * Re-runs the upload for a failed pill (Retry button). No-op on non-failed entries.
   *
   * The original placeholder was removed when the upload first failed (no useful state for a
   * gone-but-coming-back widget), so retry anchors a fresh placeholder at the current selection.
   * That's a small UX regression from "image lands at the original drop spot" — documented as
   * an accepted trade-off in the placeholder design plan.
   */
  protected retryPending(localId: string): void {
    const entry = this.pending().find((p) => p.localId === localId);
    if (entry?.status !== 'failed' || !entry.file) return;
    this.pending.update((arr) =>
      arr.map((p) =>
        p.localId === localId ? { ...p, status: 'uploading' as const, error: undefined } : p,
      ),
    );
    const ed = this.editor;
    if (ed && entry.isImage) {
      this.insertUploadPlaceholder(localId, entry.file, ed.state.selection.to);
    }
    this.runUpload(localId).catch(() => {/* error already on the pill */});
  }

  /**
   * Sets the display size on a pending image pill. For pills whose upload already succeeded the
   * image was inserted with `m` by default; resizing after the fact patches the corresponding
   * Tiptap image node so the wire payload reflects the user's final choice.
   */
  protected setPendingImageSize(localId: string, size: PendingImageSize): void {
    const entry = this.pending().find((p) => p.localId === localId);
    if (!entry?.isImage) return;
    this.pending.update((arr) =>
      arr.map((p) => (p.localId === localId ? { ...p, size } : p)),
    );
    if (entry.filestoreFileId) {
      this.updateInlineImageSize(entry.filestoreFileId, size);
    }
  }

  private removeInlineImageNode(filestoreFileId: string): void {
    const ed = this.editor;
    if (!ed) return;
    const token = `attachment:${filestoreFileId}`;
    const tr = ed.state.tr;
    const positionsToDelete: Array<{ from: number; to: number }> = [];
    ed.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs['src'] === token) {
        positionsToDelete.push({ from: pos, to: pos + node.nodeSize });
      }
      return true;
    });
    // Delete back-to-front so earlier positions stay valid as later nodes shrink the doc.
    // Clone before reversing so the lint that flags in-place `.reverse()` mutations stays happy.
    const reversed = [...positionsToDelete].reverse();
    for (const { from, to } of reversed) {
      tr.delete(from, to);
    }
    if (positionsToDelete.length > 0) ed.view.dispatch(tr);
  }

  private updateInlineImageSize(filestoreFileId: string, size: PendingImageSize): void {
    const ed = this.editor;
    if (!ed) return;
    const token = `attachment:${filestoreFileId}`;
    const tr = ed.state.tr;
    let touched = false;
    ed.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs['src'] === token) {
        // Mutate the `alt` attribute, not a class. The new alt carries the size suffix so the
        // change is part of the markdown body — `onUpdate` fires from the dispatch, the editor
        // re-serializes, `bodyMarkdownChange` emits, and the host's dirty-check enables Save
        // even when the only edit was a size swap.
        const currentAlt = (node.attrs['alt'] as string | undefined) ?? '';
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          alt: encodeSizeIntoAlt(currentAlt, size),
        });
        touched = true;
      }
      return true;
    });
    if (touched) ed.view.dispatch(tr);
  }

  /**
   * Computes and emits the union of attachment ids the host should send on submit. Sources:
   *  - inline `attachment:<uuid>` tokens currently in the body markdown,
   *  - every uploaded pending pill (image or non-image), deduped against the body set.
   *
   * Image pills are included here now that the pill row is the durable "attached" indicator
   * for *all* file types — an image dropped on the pill row (attachment-only mode) has no
   * inline body token, so the pill is the only signal that the file is attached. Pills whose
   * id is also in the body are deduped, so an inline image isn't double-counted.
   */
  private emitAttachmentIds(): void {
    const ed = this.editor;
    const body = ed
      ? (ed.storage as unknown as MarkdownStorage).markdown.getMarkdown()
      : (this.lastEmittedMarkdown ?? this.bodyMarkdown() ?? '');
    const fromBody = extractAttachmentIds(body);
    const fromPills = this.pending()
      .filter((p) => p.status === 'uploaded' && p.filestoreFileId)
      .map((p) => p.filestoreFileId as string);
    const merged = [...fromBody, ...fromPills.filter((id) => !fromBody.includes(id))];
    this.attachmentIdsChange.emit(merged);
  }

  /**
   * Toggle a link on the current selection. When already a link, removes it; otherwise prompts
   * the user for a URL via the browser's native dialog (lightweight; a styled popover is a
   * sensible follow-up but isn't worth the complexity for the initial drop).
   */
  protected toggleLink(): void {
    const ed = this.editor;
    if (!ed) return;
    if (ed.isActive('link')) {
      ed.chain().focus().unsetLink().run();
      return;
    }
    const previousUrl = ed.getAttributes('link')['href'] as string | undefined;
    const url = globalThis.prompt('Link URL', previousUrl ?? 'https://');
    if (url === null) return; // user cancelled
    if (url === '') {
      ed.chain().focus().unsetLink().run();
      return;
    }
    ed.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  private refreshActive(): void {
    const ed = this.editor;
    if (!ed) return;
    const headingLevel = activeHeadingLevel(ed);
    this.active.set({
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      strike: ed.isActive('strike'),
      code: ed.isActive('code'),
      link: ed.isActive('link'),
      blockquote: ed.isActive('blockquote'),
      bulletList: ed.isActive('bulletList'),
      orderedList: ed.isActive('orderedList'),
      codeBlock: ed.isActive('codeBlock'),
      headingLevel,
      canUndo: ed.can().undo(),
      canRedo: ed.can().redo(),
    });
  }
}

/**
 * Resolve the active heading level (1, 2, or 3) at the editor's current selection, or `null`
 * when the selection is in plain paragraph text. Extracted from the live `refreshActive` path
 * to keep the rest of the snapshot construction free of nested ternaries.
 */
function activeHeadingLevel(editor: Editor): HeadingLevel | null {
  if (editor.isActive('heading', { level: 1 })) return 1;
  if (editor.isActive('heading', { level: 2 })) return 2;
  if (editor.isActive('heading', { level: 3 })) return 3;
  return null;
}

/**
 * Pulls every file off a {@link DataTransfer} (drop or clipboard paste). All MIME types are
 * accepted — the server-side denylist enforces the real boundary
 * (`comments.attachments.blocked-mime-types`), and the UX rejects blocked types via a typed
 * `AttachmentUploadError` which mirrors back into a per-pill failure state. The caller decides
 * whether to let the browser default through when this returns an empty array.
 */
function filesFromDataTransfer(source: DataTransfer | null): File[] {
  if (!source) return [];
  return Array.from(source.files);
}

/** Display size for an inline image. Maps to a CSS class `tf-img-{s|m|l}` on the rendered img. */
export type PendingImageSize = 's' | 'm' | 'l';

type PendingStatus = 'uploading' | 'uploaded' | 'failed';

/**
 * One file's lifecycle inside the editor's pending row. Image entries get an additional `size`
 * (defaulting to `m`) that the pill exposes via three buttons; non-image entries set `size` to
 * `null`. We keep the original {@link File} around so Retry can re-submit a failed upload
 * without forcing the user to re-pick.
 */
interface PendingAttachment {
  readonly localId: string;
  readonly name: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly isImage: boolean;
  readonly status: PendingStatus;
  readonly filestoreFileId?: string;
  readonly error?: AttachmentUploadError;
  readonly file?: File;
  readonly size: PendingImageSize | null;
}

let localIdCounter = 0;
function nextLocalId(): string {
  localIdCounter += 1;
  return `pa-${localIdCounter}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Reasons whose failures are permanent — the same file uploaded again would fail the same way,
 * so the pill's Retry button is suppressed and the user can only Remove. Recoverable reasons
 * (`upload-rejected`, `upload-timeout`, `unknown`) keep Retry. Mirrors the server-side
 * `CommentAttachmentLimits` enforcement: MIME, size, count, and tenant-storage caps are all
 * deterministic checks that retry can't change.
 */
const NON_RECOVERABLE_REASONS: ReadonlySet<AttachmentUploadErrorReason> = new Set([
  'mime-not-allowed',
  'too-large',
  'storage-limit-exceeded',
  'too-many-attachments',
]);

export function isRecoverableUploadFailure(error: AttachmentUploadError | undefined): boolean {
  if (!error) return false;
  return !NON_RECOVERABLE_REASONS.has(error.reason);
}

/**
 * Cache key for an upload-placeholder's local preview object URL inside `localBlobUrls`. The
 * cache is also used post-upload (keyed by `filestoreFileId`) so the inline `<img>` resolves;
 * prefixing keeps the two name spaces from colliding when a single upload moves from pending
 * (keyed by local id) to uploaded (keyed by filestore id).
 */
function localPreviewKey(localId: string): string {
  return `local-preview:${localId}`;
}
